// Field validators — patterns from SUPPLIER_PORTAL_BRIEF.md §10.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\(\d{3}\) \d{3}-\d{4}$/;
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_RE = /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/;

export const ALLOWED_LOGO_EXTENSIONS = ["jpg", "jpeg", "png"] as const;
export const ALLOWED_DOCUMENT_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "csv",
  "jpg",
  "jpeg",
  "png",
] as const;
export const BLOCKED_EXTENSIONS = [
  "exe",
  "msi",
  "bat",
  "cmd",
  "ps1",
  "sh",
  "js",
  "mjs",
  "vbs",
  "jar",
  "zip",
  "rar",
  "7z",
  "tar",
  "gz",
  "iso",
  "dll",
  "app",
  "dmg",
] as const;

export const MAX_LOGO_BYTES = 10 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 50 * 1024 * 1024;

export const isEmail = (v: string) => EMAIL_RE.test(v);
export const isPhone = (v: string) => PHONE_RE.test(v);

export function isZipForCountry(v: string, country: string | undefined): boolean {
  if (country === "Canada") return CA_POSTAL_RE.test(v.toUpperCase());
  return US_ZIP_RE.test(v);
}

export function normalizeWebsite(v: string): string {
  if (!v) return v;
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

/**
 * Format a phone number to NANP `(###) ###-####` as the user types.
 *
 * Strips all non-digits, takes the first 10, and progressively applies the
 * mask so partial values render as `(800`, `(800) 7`, `(800) 720-`, etc.
 * Idempotent — feeding `(800) 720-5756` back in produces the same string,
 * so we can also normalize prefilled data from D365 (which has formats like
 * `(800)720-5756` without the post-area-code space).
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function fileExtension(filename: string): string {
  const idx = filename.lastIndexOf(".");
  return idx === -1 ? "" : filename.slice(idx + 1).toLowerCase();
}

export function validateLogo(file: File): string | null {
  const ext = fileExtension(file.name);
  if (!ALLOWED_LOGO_EXTENSIONS.includes(ext as typeof ALLOWED_LOGO_EXTENSIONS[number])) {
    return `Logo must be one of: ${ALLOWED_LOGO_EXTENSIONS.join(", ")}`;
  }
  if (file.size > MAX_LOGO_BYTES) return "Logo must be 10 MB or smaller";
  return null;
}

export function validateDocument(file: File): string | null {
  const ext = fileExtension(file.name);
  if (BLOCKED_EXTENSIONS.includes(ext as typeof BLOCKED_EXTENSIONS[number])) {
    return `File type "${ext}" is not permitted`;
  }
  if (!ALLOWED_DOCUMENT_EXTENSIONS.includes(ext as typeof ALLOWED_DOCUMENT_EXTENSIONS[number])) {
    return `Document must be one of: ${ALLOWED_DOCUMENT_EXTENSIONS.join(", ")}`;
  }
  if (file.size > MAX_DOCUMENT_BYTES) return "Document must be 50 MB or smaller";
  return null;
}
